import { useEffect, useCallback, useState } from 'react'
import { Message } from '@/types'
import { websocketService } from '@/services/websocket'

interface UseWebSocketOptions {
  conversationId: number
  onMessage?: (message: Message) => void
  onTyping?: (userId: number, isTyping: boolean) => void
  onRead?: (messageId: number, userId: number) => void
  onActionChoice?: (message: string) => void
}

export function useWebSocket({
  conversationId,
  onMessage,
  onTyping,
  onRead,
  onActionChoice,
}: UseWebSocketOptions) {
  const [isConnected, setIsConnected] = useState(false)

  useEffect(() => {
    const token = localStorage.getItem('access_token')
    const refreshToken = localStorage.getItem('refresh_token')

    if (!token) {
      console.error('No access token found - user may not be logged in')
      return
    }

    // Don't connect if conversationId is invalid
    if (!conversationId || conversationId === 0) {
      console.warn('Invalid conversationId, skipping WebSocket connection')
      return
    }

    // If we have a refresh token, try to get a fresh access token before connecting
    const ensureFreshToken = async () => {
      if (refreshToken) {
        try {
          // Use the VITE_API_URL environment variable
          const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'
          const response = await fetch(`${apiUrl}/auth/token/refresh/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh: refreshToken }),
          })

          if (response.ok) {
            const data = await response.json()
            const newToken = data.access
            localStorage.setItem('access_token', newToken)
            console.log('Access token refreshed before WebSocket connection')
            return newToken
          } else {
            console.warn('Failed to refresh token, using existing token')
            return token
          }
        } catch (err) {
          console.warn('Could not refresh token, using existing token:', err)
          return token
        }
      }
      return token
    }

    // Connect with fresh token
    ensureFreshToken().then((freshToken) => {
      websocketService.connect(conversationId, freshToken)
      setIsConnected(websocketService.isConnected())
    })

    // Check connection status periodically
    const connectionCheck = setInterval(() => {
      setIsConnected(websocketService.isConnected())
    }, 1000)

    // Cleanup on unmount
    return () => {
      clearInterval(connectionCheck)
      websocketService.disconnect()
      setIsConnected(false)
    }
  }, [conversationId])

  useEffect(() => {
    if (!onMessage) return
    return websocketService.onMessage(onMessage)
  }, [onMessage])

  useEffect(() => {
    if (!onTyping) return
    return websocketService.onTyping(onTyping)
  }, [onTyping])

  useEffect(() => {
    if (!onRead) return
    return websocketService.onRead(onRead)
  }, [onRead])

  useEffect(() => {
    if (!onActionChoice) return
    return websocketService.onActionChoice(onActionChoice)
  }, [onActionChoice])

  const sendMessage = useCallback(
    (content: string) => {
      websocketService.sendMessage(content)
    },
    []
  )

  const sendTyping = useCallback((isTyping: boolean) => {
    websocketService.sendTyping(isTyping)
  }, [])

  const markAsRead = useCallback((messageId: number) => {
    websocketService.markAsRead(messageId)
  }, [])

  return {
    isConnected,
    sendMessage,
    sendTyping,
    markAsRead,
  }
}
